import React from 'react';
import { motion } from 'framer-motion';
import { 
  ShieldCheckIcon,
  UserIcon,
  DocumentTextIcon,
  LockClosedIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  EnvelopeIcon,
  MapPinIcon,
  ShieldExclamationIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

const PrivacyPolicy: React.FC = () => {
  const effectiveDate = 'May 15, 2026';

  return (
    <div className="min-h-screen py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 text-xs font-semibold text-primary bg-primary/10 border border-primary/20 rounded-full mb-4">
            <ShieldCheckIcon className="w-4 h-4" />
            Official Policy
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-text-main mb-4 tracking-tight leading-tight">
            Privacy Policy
          </h1>
          <p className="text-lg text-text-muted max-w-2xl mx-auto">
            Effective Date: <span className="text-text-main font-medium">{effectiveDate}</span>  ·  Applies to all NotesCrafter platforms
          </p>
        </motion.div>

        {/* Intro Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-glass/30 backdrop-blur-md border border-border-glow rounded-2xl p-8 mb-10 hover:border-primary/50 transition-colors shadow-xl"
        >
          <div className="flex flex-col md:flex-row items-start gap-6">
            <div className="bg-gradient-to-br from-primary/20 to-secondary/20 border border-primary/30 p-4 rounded-xl shadow-inner">
              <ShieldCheckIcon className="w-8 h-8 text-primary" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-text-main mb-3">1. Introduction</h2>
              <p className="text-text-muted leading-relaxed">
                NotesCrafter (<strong className="text-text-main font-medium">"we", "our", "us"</strong>) is a sole proprietorship operating the platforms <code className="px-1.5 py-0.5 text-xs text-primary bg-primary/10 border border-primary/20 rounded">flow.notescrafter.com</code>, <code className="px-1.5 py-0.5 text-xs text-primary bg-primary/10 border border-primary/20 rounded">tools.notescrafter.com</code>, <code className="px-1.5 py-0.5 text-xs text-primary bg-primary/10 border border-primary/20 rounded">print.notescrafter.com</code>, and the NotesCrafter Android application.
              </p>
              <p className="text-text-muted leading-relaxed mt-3">
                This Privacy Policy describes how we collect, use, store, and protect your personal information. By using any NotesCrafter service, you agree to this Privacy Policy.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Detailed Sections */}
        <div className="space-y-8">
          
          {/* Section 2: Eligibility */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-glass/20 backdrop-blur-sm border border-border-glow rounded-xl p-8 hover:border-primary/30 transition-colors shadow-lg"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="bg-primary/10 p-2 rounded-lg border border-primary/20">
                <UserIcon className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-text-main">2. Eligibility & Age</h2>
            </div>
            <div className="pl-0 sm:pl-12 space-y-4">
              <p className="text-text-muted leading-relaxed">
                Our services are intended for users aged 13 and above. By using NotesCrafter, you confirm that you are at least 13 years old. If you are under 18 years of age, you must have the consent of a parent or legal guardian to use our services.
              </p>
              <p className="text-text-muted leading-relaxed">
                We do not knowingly collect personal information from children under 13. If you believe a child under 13 has provided us with personal information, please contact us immediately at <a href="mailto:support@notescrafter.com" className="text-primary hover:underline font-medium">support@notescrafter.com</a>.
              </p>
            </div>
          </motion.div>

          {/* Section 3: Information We Collect */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-glass/20 backdrop-blur-sm border border-border-glow rounded-xl p-8 hover:border-primary/30 transition-colors shadow-lg"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="bg-primary/10 p-2 rounded-lg border border-primary/20">
                <DocumentTextIcon className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-text-main">3. Information We Collect</h2>
            </div>
            
            <div className="pl-0 sm:pl-12 space-y-6">
              {/* 3.1 */}
              <div className="border-l-2 border-primary/30 pl-4 py-1">
                <h3 className="text-lg font-bold text-text-main mb-2">3.1 Google Login (All Platforms)</h3>
                <p className="text-text-muted leading-relaxed">
                  When you sign in using Google, we receive your name, email address, and Google profile photo. This information is used solely for account identification and communication purposes.
                </p>
              </div>

              {/* 3.2 */}
              <div className="border-l-2 border-primary/30 pl-4 py-1">
                <h3 className="text-lg font-bold text-text-main mb-2">3.2 Print Orders — Website & Android App</h3>
                <p className="text-text-muted leading-relaxed">
                  For the purpose of processing and delivering your print order, we collect:
                </p>
                <ul className="list-disc list-inside text-text-muted mt-2 space-y-1.5">
                  <li><span className="text-text-main font-medium">Full delivery address</span></li>
                  <li><span className="text-text-main font-medium">Phone number</span></li>
                </ul>
                <p className="text-text-muted leading-relaxed mt-2">
                  This data is stored securely and is used only to fulfil your order and communicate delivery updates via our shipping partner, <span className="text-text-main font-semibold">Shiprocket</span>.
                </p>
              </div>

              {/* 3.3 */}
              <div className="border-l-2 border-primary/30 pl-4 py-1">
                <h3 className="text-lg font-bold text-text-main mb-2">3.3 Uploaded Files — Print Service Only</h3>
                <p className="text-text-muted leading-relaxed">
                  PDF files you upload through the print service (via website or Android app) are stored on our servers solely to process and fulfil your print order. These files are <strong className="text-red-400 font-semibold">permanently and irreversibly deleted 30 days</strong> from the date of delivery. We do not use your uploaded content for any other purpose.
                </p>
              </div>

              {/* 3.4 */}
              <div className="border-l-2 border-green-500/30 pl-4 py-1 bg-green-500/5 rounded-r-lg pr-4">
                <h3 className="text-lg font-bold text-green-400 mb-2 flex items-center gap-2">
                  <ShieldCheckIcon className="w-5 h-5 text-green-400" />
                  3.4 What We Do NOT Collect
                </h3>
                <p className="text-text-muted leading-relaxed">
                  <span className="text-text-main font-semibold">Flow</span> and <span className="text-text-main font-semibold">Tools</span> — whether accessed via web or the Android app — perform all document processing entirely within your browser or on your device. No files, documents, or processing data are ever transmitted to or stored on our servers. This is a <span className="text-green-400 font-semibold">zero server-touch architecture</span>.
                </p>
                <p className="text-text-muted leading-relaxed mt-2">
                  We do not collect or store any files or document content from Flow or Tools on any platform.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Section 4: How We Use Your Information */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-glass/20 backdrop-blur-sm border border-border-glow rounded-xl p-8 hover:border-primary/30 transition-colors shadow-lg"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="bg-primary/10 p-2 rounded-lg border border-primary/20">
                <EyeIcon className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-text-main">4. How We Use Your Information</h2>
            </div>
            <div className="pl-0 sm:pl-12">
              <p className="text-text-muted leading-relaxed mb-4">
                We use the information we collect to:
              </p>
              <ul className="space-y-3">
                {[
                  'Authenticate your identity via Google Login',
                  'Process and deliver print orders',
                  'Communicate order status updates and support responses',
                  'Manage your Community Pass membership and access',
                  'Calculate prorated upgrade discounts for Community Pass tier changes',
                  'Improve and maintain our services'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-text-muted">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* Section 5: Third-Party Services */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-glass/20 backdrop-blur-sm border border-border-glow rounded-xl p-8 hover:border-primary/30 transition-colors shadow-lg"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="bg-primary/10 p-2 rounded-lg border border-primary/20">
                <LockClosedIcon className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-text-main">5. Third-Party Services</h2>
            </div>
            <div className="pl-0 sm:pl-12 space-y-4">
              <p className="text-text-muted leading-relaxed">
                We share your data with the following trusted service providers only to the extent necessary for service delivery:
              </p>
              <ul className="space-y-3">
                {[
                  { name: 'Google', purpose: 'Authentication (Google OAuth)' },
                  { name: 'Razorpay', purpose: 'Payment processing for Community Pass and print orders' },
                  { name: 'Shiprocket', purpose: 'Order shipping, tracking, and logistics for print orders' },
                  { name: 'Google AdMob', purpose: 'In-app advertising for free users on Android' }
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-text-muted">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                    <span>
                      <strong className="text-text-main font-semibold">{item.name}</strong> — {item.purpose}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="text-text-muted leading-relaxed mt-4 bg-primary/5 p-4 rounded-lg border border-primary/10">
                Each of these services operates under their own privacy policies. We do not sell, rent, or trade your personal data to any third party for marketing purposes.
              </p>
            </div>
          </motion.div>

          {/* Section 6: Data Retention */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-glass/20 backdrop-blur-sm border border-border-glow rounded-xl p-8 hover:border-primary/30 transition-colors shadow-lg"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="bg-primary/10 p-2 rounded-lg border border-primary/20">
                <ArrowPathIcon className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-text-main">6. Data Retention</h2>
            </div>
            <div className="pl-0 sm:pl-12">
              <ul className="space-y-4">
                {[
                  { label: 'Google login data (name, email, photo)', detail: 'Retained while your account is active. Deleted upon your account deletion request.' },
                  { label: 'Delivery address and phone number', detail: 'Retained until you request deletion by contacting support@notescrafter.com.' },
                  { label: 'Print order PDF files', detail: 'Permanently deleted 30 days after the date of delivery.' },
                  { label: 'Flow / Tools processing data', detail: 'Not collected or stored. Zero server touch.' },
                  { label: 'Payment and transaction records', detail: 'Retained as required by applicable Indian tax and financial regulations.' }
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-text-muted border-l-2 border-primary/20 pl-4">
                    <div>
                      <strong className="text-text-main block mb-1 font-semibold">{item.label}</strong>
                      <span className="text-sm text-text-muted/90">{item.detail}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* Section 7: Your Rights under DPDPA 2023 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="bg-glass/20 backdrop-blur-sm border border-border-glow rounded-xl p-8 hover:border-primary/30 transition-colors shadow-lg"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="bg-primary/10 p-2 rounded-lg border border-primary/20">
                <ShieldExclamationIcon className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-text-main">7. Your Rights under DPDPA 2023</h2>
            </div>
            <div className="pl-0 sm:pl-12 space-y-4">
              <p className="text-text-muted leading-relaxed">
                Under the Digital Personal Data Protection Act, 2023 (India), you have the right to:
              </p>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                {[
                  'Access the personal data we hold about you',
                  'Correct inaccurate or incomplete data',
                  'Request deletion (erasure) of your data',
                  'Withdraw consent at any time',
                  'Nominate a person to exercise rights'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-text-muted bg-primary/5 px-4 py-2 border border-primary/10 rounded-lg">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                    <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-text-muted leading-relaxed">
                To exercise any of these rights, please write to us at <a href="mailto:support@notescrafter.com" className="text-primary hover:underline font-medium">support@notescrafter.com</a>. We will respond within a reasonable timeframe.
              </p>
            </div>
          </motion.div>

          {/* Section 8: Cookies & Advertising */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="bg-glass/20 backdrop-blur-sm border border-border-glow rounded-xl p-8 hover:border-primary/30 transition-colors shadow-lg"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="bg-primary/10 p-2 rounded-lg border border-primary/20">
                <DocumentTextIcon className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-text-main">8. Cookies & Advertising</h2>
            </div>
            <div className="pl-0 sm:pl-12 space-y-4 text-text-muted leading-relaxed">
              <p>
                We use minimal cookies for session management and to maintain your login state. We do not use third-party advertising or tracking cookies on our web platform.
              </p>
              <p>
                The NotesCrafter Android app serves advertisements to free users via Google AdMob. AdMob may collect device identifiers and usage data in accordance with Google's Privacy Policy. <strong className="text-primary font-semibold">Community Pass holders on Android are completely ad-free across all features.</strong>
              </p>
            </div>
          </motion.div>

          {/* Section 9: Security */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85 }}
            className="bg-glass/20 backdrop-blur-sm border border-border-glow rounded-xl p-8 hover:border-primary/30 transition-colors shadow-lg"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="bg-primary/10 p-2 rounded-lg border border-primary/20">
                <LockClosedIcon className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-text-main">9. Security</h2>
            </div>
            <div className="pl-0 sm:pl-12 text-text-muted leading-relaxed">
              <p>
                We implement industry-standard security measures to protect your personal data. However, no method of data transmission or storage over the internet is completely secure. We cannot guarantee absolute security, but we are committed to protecting your information to the best of our ability.
              </p>
            </div>
          </motion.div>

          {/* Section 10: Changes to This Policy */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            className="bg-glass/20 backdrop-blur-sm border border-border-glow rounded-xl p-8 hover:border-primary/30 transition-colors shadow-lg"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="bg-primary/10 p-2 rounded-lg border border-primary/20">
                <ExclamationTriangleIcon className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-text-main">10. Changes to This Policy</h2>
            </div>
            <div className="pl-0 sm:pl-12 text-text-muted leading-relaxed">
              <p>
                We may update this Privacy Policy from time to time. We will notify you of material changes via WhatsApp, email, or a prominent notice on our platform. The "Effective Date" at the top of this page indicates when the latest version was published. Continued use of our services after changes constitutes your acceptance of the updated policy.
              </p>
            </div>
          </motion.div>

          {/* Section 11: Contact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95 }}
            className="bg-glass/30 backdrop-blur-md border border-primary/20 rounded-xl p-8 hover:border-primary/40 transition-colors shadow-xl"
          >
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
              <div>
                <h2 className="text-2xl font-bold text-text-main mb-2">11. Contact Us</h2>
                <div className="space-y-1 text-text-muted text-sm sm:text-base">
                  <p className="font-semibold text-text-main">NotesCrafter</p>
                  <p className="flex items-center gap-1.5">
                    <MapPinIcon className="w-4 h-4 text-primary flex-shrink-0" />
                    Sole Proprietorship · Aurangabad, Bihar, India
                  </p>
                </div>
              </div>
              <a
                href="mailto:support@notescrafter.com"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary hover:bg-primary-hover text-white font-semibold rounded-xl transition-all shadow-lg shadow-primary/25 hover:scale-[1.02] active:scale-[0.98]"
              >
                <EnvelopeIcon className="w-5 h-5" />
                support@notescrafter.com
              </a>
            </div>
          </motion.div>

        </div>

        {/* Bottom Warning/Updates box */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
          className="mt-12 bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6"
        >
          <div className="flex items-start space-x-3">
            <ExclamationTriangleIcon className="w-6 h-6 text-yellow-300 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-yellow-200 mb-1">Policy Updates</h3>
              <p className="text-yellow-200/80 text-sm leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any significant changes by posting the new policy on this page and updating the "Last updated" date. We encourage you to review this policy periodically.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;